import { CONDITION_OPS, type Condition, type ConditionOp, type Settings } from "@/lib/integrations/settings";

import { Combobox, type Suggestion } from "./Combobox";
import { Input, Segmented, Select } from "./controls";
import styles from "./integrations.module.css";
import { Row, Rows } from "./Rows";

function takes(op: ConditionOp) {
  return CONDITION_OPS.find((one) => one.op === op)?.takes ?? "one";
}

/** Keep rules: column · test · value, and whether all or any must match. */
export function ConditionList({
  keep,
  columns,
  onChange,
  disabled = false,
}: {
  keep: Settings["keep"];
  columns: Suggestion[];
  onChange: (keep: Settings["keep"]) => void;
  disabled?: boolean;
}) {
  const set = (conditions: Condition[]) => onChange({ ...keep, conditions });
  const update = (index: number, patch: Partial<Condition>) =>
    set(keep.conditions.map((condition, i) => (i === index ? { ...condition, ...patch } : condition)));
  return (
    <div style={{ display: "grid", gap: "var(--s3)" }}>
      {keep.conditions.length > 1 && (
        <div className={styles.inline}>
          <span className={styles.muted}>Keep a row when</span>
          <Segmented<"all" | "any">
            name="All or any"
            value={keep.mode}
            options={[
              { value: "all", label: "every rule matches" },
              { value: "any", label: "any rule matches" },
            ]}
            onChange={(mode) => onChange({ ...keep, mode })}
          />
        </div>
      )}
      <Rows
        count={keep.conditions.length}
        empty="No keep rules. Every row is kept."
        addLabel="Add a keep rule"
        onAdd={disabled ? undefined : () => set([...keep.conditions, { column: "", op: "not_empty" }])}
        onMove={(from, to) => {
          const next = [...keep.conditions];
          const [moved] = next.splice(from, 1);
          next.splice(to, 0, moved);
          set(next);
        }}
        onRemove={(index) => set(keep.conditions.filter((_, i) => i !== index))}
      >
        {keep.conditions.map((condition, index) => {
          const kind = takes(condition.op);
          return (
            <Row key={index} index={index}>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(10rem, 1fr) minmax(9rem, auto) minmax(10rem, 1fr)", gap: "var(--s2)", width: "100%" }}>
                <Combobox
                  value={condition.column}
                  placeholder="Column"
                  suggestions={columns}
                  disabled={disabled}
                  onChange={(column) => update(index, { column })}
                />
                <Select
                  aria-label="Test"
                  value={condition.op}
                  disabled={disabled}
                  onChange={(event) => update(index, { op: event.target.value as ConditionOp })}
                >
                  {CONDITION_OPS.map((one) => (
                    <option key={one.op} value={one.op}>
                      {one.label}
                    </option>
                  ))}
                </Select>
                {kind === "one" && (
                  <Input
                    aria-label="Value"
                    value={String(condition.value ?? "")}
                    disabled={disabled}
                    placeholder="Value"
                    onChange={(event) => update(index, { value: event.target.value })}
                  />
                )}
                {kind === "many" && (
                  <Input
                    aria-label="Values, separated by commas"
                    value={(condition.values ?? []).map(String).join(", ")}
                    disabled={disabled}
                    placeholder="CER, OCT"
                    onChange={(event) =>
                      update(index, {
                        values: event.target.value
                          .split(",")
                          .map((part) => part.trim())
                          .filter((part, i, all) => part !== "" || i === all.length - 1),
                      })
                    }
                  />
                )}
                {kind === "two" && (
                  <span className={styles.inline} style={{ flexWrap: "nowrap" }}>
                    <Input
                      aria-label="From"
                      value={String(condition.values?.[0] ?? "")}
                      disabled={disabled}
                      onChange={(event) => update(index, { values: [event.target.value, condition.values?.[1] ?? ""] })}
                    />
                    and
                    <Input
                      aria-label="To"
                      value={String(condition.values?.[1] ?? "")}
                      disabled={disabled}
                      onChange={(event) => update(index, { values: [condition.values?.[0] ?? "", event.target.value] })}
                    />
                  </span>
                )}
                {kind === "none" && <span />}
              </div>
            </Row>
          );
        })}
      </Rows>
    </div>
  );
}
